import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useQueryClient } from "react-query";
import PortOne from "@portone/browser-sdk/v2";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";
import { PASS_VERIFIED_DATE_OF_BIRTH_COOKIE, PASS_VERIFIED_NAME, PASS_VERIFIED_PHONE_COOKIE, setAgeVerificationUuid, setExpiringAgeVerification, setPassVerifiedData, } from "@jti/fe-common";
import { getSessionStorage, useAvpActions, usePolling } from "@jti/rest-api";
import { Dialog } from "../../components/Dialog";
import { useCounters } from "../../hooks/useCounters/useCounters";
import { useCurrentBreakpoint } from "../../hooks/useCurrentBreakpoint";
import { useDeleteElementMutationObserver } from "../../hooks/useDeleteElementMutationObserver";
import { useHavOverlayConditions } from "../../hooks/useHavOverlayConditions";
import { useConfig } from "../../providers/useConfig";
import { ActionButtons, AgeVerificationContainer, BackButton, PopupImage, } from "./components";
import { AGE_VERIFICATION_METHOD, AGE_VERIFICATION_STEPS, } from "./HardAgeVerificationPopup.types";
import styles from "./HardAgeVerificationPopup.module.scss";
const { sanitize } = dompurify;
const defaultCookieTtlFallback = 86400;
const defaultQrTtlFallback = 180;
const fallbackCommonError = "Something went wrong. Please try again later.";
const hardAgeVerificationModalId = "hardAgeVerificationModal";
const TRIGGERED_AGE_VERIFICATION = "triggeredAgeVerification";
export const HardAgeVerificationPopup = ({ externalContent, logoSrc, title, description, continueButtonLabel, exitButtonLabel, legalNotice, alt, desktopImageSrc, tabletImageSrc, mobileImageSrc, logoAlt, exitButtonLink, backButtonLabel, verificationTitle, mobileQrHelper, verificationSteps, getNewQrButtonLabel, ttlOfCookie = defaultCookieTtlFallback, qrStaleError, qrCommonError = fallbackCommonError, qrTtl = defaultQrTtlFallback, failedProviderVerificationError = fallbackCommonError, backgroundColor, backgroundBlur, 
// adding PASS for AV for KOR market
helperText, signInURL, ageVerificationMethod = AGE_VERIFICATION_METHOD.DIIA_ASYNC, waitingMessage, signInButtonLabel, tryAgainButtonLabel, verificationErrorHelperText, missingDataFromAvError = fallbackCommonError, portalDomNode, 
// Change phone number props
isOpened = false, isPersonalDataUpdatePending = false, isChangePersonalData = false, ignoreUrls = [], onClose, onAgeVerificationSuccess, }) => {
    const config = useConfig();
    const { shouldRenderHAV, shouldTriggerAv, isRegisterPage } = useHavOverlayConditions({
        ignoreUrls,
        ageVerificationMethod,
    });
    const [isModalOpened, setIsModalOpened] = useState(shouldRenderHAV);
    const queryClient = useQueryClient();
    const { startPolling, stopPolling } = usePolling();
    const { isMedium, isSmall, isLarge } = useCurrentBreakpoint();
    const { remainingTime, resetTimer, clearTimer: stopStaleTimer } = useCounters(Number(qrTtl));
    const { initAsyncVerification, asyncVerificationData, asyncVerificationPending, verify } = useAvpActions();
    const [isQrStale, setIsQrStale] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const [currentStep, setCurrentStep] = useState(AGE_VERIFICATION_STEPS.DISCLAIMER);
    useDeleteElementMutationObserver({ elementId: hardAgeVerificationModalId, isMutationObserverActive: isModalOpened });
    const isAgeVerificationInProgress = currentStep === AGE_VERIFICATION_STEPS.AGE_VERIFICATION;
    const isPassVerification = ageVerificationMethod === AGE_VERIFICATION_METHOD.PASS;
    const isButtonsVisible = !isAgeVerificationInProgress || (isPassVerification && !!errorMessage);
    const isTopBackButtonVisible = isAgeVerificationInProgress && !isPassVerification;
    const isHardAgeVerificationPopupOpened = isChangePersonalData ? isOpened : isModalOpened;
    const isPrimaryButtonVisible = !isChangePersonalData || (isChangePersonalData && isAgeVerificationInProgress);
    const descriptionContent = useMemo(() => {
        if (isPassVerification && isAgeVerificationInProgress) {
            return waitingMessage;
        }
        return description;
    }, [description, isAgeVerificationInProgress, isPassVerification, waitingMessage]);
    const primaryButtonLabel = useMemo(() => {
        if (errorMessage)
            return backButtonLabel;
        return isPassVerification ? signInButtonLabel : continueButtonLabel;
    }, [backButtonLabel, continueButtonLabel, errorMessage, isPassVerification, signInButtonLabel]);
    const secondaryButtonLabel = useMemo(() => {
        if (errorMessage)
            return tryAgainButtonLabel;
        return isPassVerification ? continueButtonLabel : exitButtonLabel;
    }, [continueButtonLabel, errorMessage, exitButtonLabel, isPassVerification, tryAgainButtonLabel]);
    const modalImage = useMemo(() => {
        if (isSmall && mobileImageSrc) {
            return mobileImageSrc;
        }
        if (isMedium && tabletImageSrc) {
            return tabletImageSrc;
        }
        return desktopImageSrc;
    }, [desktopImageSrc, isMedium, isSmall, mobileImageSrc, tabletImageSrc]);
    const handlePassAgeVerification = useCallback((consumer_data) => {
        if (!consumer_data?.date_of_birth || !consumer_data?.phone || !consumer_data?.name) {
            toast.error(missingDataFromAvError);
            throw new Error(missingDataFromAvError);
        }
        const { date_of_birth, phone, name } = consumer_data;
        setPassVerifiedData(name, PASS_VERIFIED_NAME, Number(ttlOfCookie));
        setPassVerifiedData(phone, PASS_VERIFIED_PHONE_COOKIE, Number(ttlOfCookie));
        setPassVerifiedData(date_of_birth, PASS_VERIFIED_DATE_OF_BIRTH_COOKIE, Number(ttlOfCookie));
    }, [missingDataFromAvError, ttlOfCookie]);
    const checkVerificationStatus = useCallback(async (uuid) => {
        try {
            const data = {
                country_code: config.ISO_COUNTRY_CODE,
                method_code: ageVerificationMethod,
                request_uuid: uuid,
            };
            const { is_verified, verification_performed, consumer_data } = await verify(data);
            if (!verification_performed) {
                return;
            }
            if (!is_verified && verification_performed) {
                throw new Error(failedProviderVerificationError);
            }
            if (isPassVerification) {
                handlePassAgeVerification(consumer_data);
            }
            stopPolling();
            setIsModalOpened(false);
            setAgeVerificationUuid(uuid, Number(ttlOfCookie));
            setExpiringAgeVerification(Number(ttlOfCookie));
            if (isChangePersonalData) {
                onAgeVerificationSuccess?.();
                onClose?.();
            }
        }
        catch (error) {
            setErrorMessage(error?.message || qrCommonError);
            stopPolling();
            stopStaleTimer();
        }
    }, [
        config.ISO_COUNTRY_CODE,
        ageVerificationMethod,
        verify,
        isPassVerification,
        stopPolling,
        ttlOfCookie,
        isChangePersonalData,
        failedProviderVerificationError,
        handlePassAgeVerification,
        onAgeVerificationSuccess,
        onClose,
        qrCommonError,
        stopStaleTimer,
    ]);
    const getAgeVerificationData = useCallback(async (identityVerificationIdFromParams) => {
        try {
            setErrorMessage(undefined);
            const identityVerificationId = identityVerificationIdFromParams || `identity-verification-${crypto?.randomUUID()}`;
            if (isPassVerification && !identityVerificationIdFromParams) {
                await PortOne.requestIdentityVerification({
                    identityVerificationId,
                    storeId: config.PORTONE_STORE_ID,
                    channelKey: config.PORTONE_CHANNEL_KEY,
                    windowType: {
                        mobile: "REDIRECTION",
                    },
                    redirectUrl: location.href,
                });
            }
            const { uuid } = await initAsyncVerification({
                methodCode: ageVerificationMethod,
                fields: isPassVerification
                    ? {
                        identity_verification_id: identityVerificationId,
                    }
                    : undefined,
            });
            resetTimer();
            setIsQrStale(false);
            startPolling(() => checkVerificationStatus(uuid), 5000);
        }
        catch (error) {
            setErrorMessage(error?.message || qrCommonError);
        }
    }, [
        isPassVerification,
        initAsyncVerification,
        ageVerificationMethod,
        resetTimer,
        startPolling,
        config.PORTONE_STORE_ID,
        config.PORTONE_CHANNEL_KEY,
        checkVerificationStatus,
        qrCommonError,
    ]);
    const triggerAgeVerification = useCallback(() => {
        setCurrentStep(AGE_VERIFICATION_STEPS.AGE_VERIFICATION);
        getAgeVerificationData();
    }, [getAgeVerificationData]);
    const exitFromThePage = () => {
        window.location.href = exitButtonLink;
        stopPolling();
        stopStaleTimer();
    };
    const handleNavigateToSignIn = useCallback(() => {
        window.location.href = signInURL || config.LOGIN_PAGE_URL;
        stopPolling();
        stopStaleTimer();
    }, [config.LOGIN_PAGE_URL, signInURL, stopPolling, stopStaleTimer]);
    const handleBackButtonClick = useCallback(() => {
        if (isRegisterPage) {
            handleNavigateToSignIn();
        }
        setErrorMessage(undefined);
        setCurrentStep(AGE_VERIFICATION_STEPS.DISCLAIMER);
        queryClient?.resetQueries({ queryKey: ["initAsyncVerification"] });
        stopPolling();
        stopStaleTimer();
    }, [handleNavigateToSignIn, isRegisterPage, queryClient, stopPolling, stopStaleTimer]);
    const handlePrimaryButtonClick = () => {
        if (errorMessage) {
            handleBackButtonClick();
            return;
        }
        switch (ageVerificationMethod) {
            case AGE_VERIFICATION_METHOD.DIIA_ASYNC:
                triggerAgeVerification();
                break;
            case AGE_VERIFICATION_METHOD.PASS:
                handleNavigateToSignIn();
                break;
        }
    };
    const handleSecondaryButtonClick = () => {
        if (errorMessage) {
            triggerAgeVerification();
            return;
        }
        switch (ageVerificationMethod) {
            case AGE_VERIFICATION_METHOD.DIIA_ASYNC:
                exitFromThePage();
                break;
            case AGE_VERIFICATION_METHOD.PASS:
                triggerAgeVerification();
                break;
        }
    };
    const handleRemoveParamsFromUrl = useCallback(() => {
        const url = window.location.href.split("?")[0];
        window.history.replaceState({}, "", url);
    }, []);
    const renderAvMethodProps = {
        errorMessage,
        mobileQrHelper,
        verificationSteps,
        verificationTitle,
        isPassVerification,
        getNewQrButtonLabel,
        verificationErrorHelperText,
        verificationHelperText: helperText,
        isLoading: asyncVerificationPending || isPersonalDataUpdatePending,
        getNewCode: triggerAgeVerification,
        qrCodeValue: asyncVerificationData?.data.deeplink || "",
        verificationErrorMessage: failedProviderVerificationError,
    };
    useEffect(() => {
        if (remainingTime === 0 && isQrStale === false && !asyncVerificationPending) {
            stopPolling();
            setIsQrStale(true);
            setErrorMessage(qrStaleError);
        }
    }, [remainingTime, isQrStale, asyncVerificationPending, qrStaleError, stopPolling]);
    useEffect(() => {
        if (isPassVerification) {
            const windowParams = new URLSearchParams(window.location.search);
            const identityVerificationId = windowParams.get("identityVerificationId");
            const message = windowParams.get("message");
            if (message) {
                setCurrentStep(AGE_VERIFICATION_STEPS.AGE_VERIFICATION);
                setErrorMessage(message);
                handleRemoveParamsFromUrl();
                return;
            }
            if (identityVerificationId) {
                getAgeVerificationData(identityVerificationId);
                setCurrentStep(AGE_VERIFICATION_STEPS.AGE_VERIFICATION);
                handleRemoveParamsFromUrl();
            }
        }
    }, [isPassVerification, getAgeVerificationData, handleRemoveParamsFromUrl]);
    useEffect(() => {
        setIsModalOpened(shouldRenderHAV);
    }, [shouldRenderHAV]);
    useEffect(() => {
        // sessions storage is used to prevent auto-triggering AV when user is already in the process of AV - used for mobile redirection flow
        const isPassAvAutoTriggered = getSessionStorage().get(TRIGGERED_AGE_VERIFICATION, false);
        if (shouldTriggerAv && !isAgeVerificationInProgress && !errorMessage && !isPassAvAutoTriggered) {
            getSessionStorage().set(TRIGGERED_AGE_VERIFICATION, true);
            triggerAgeVerification();
        }
    }, [errorMessage, isAgeVerificationInProgress, shouldTriggerAv, triggerAgeVerification]);
    if (!queryClient) {
        console.error("QueryClient is not provided");
        return null;
    }
    return (_jsx(Dialog, { externalContent: externalContent, isOpened: isHardAgeVerificationPopupOpened, closable: isChangePersonalData, onClose: onClose, dialogClassName: styles.dialogContainer, className: classNames(styles.dialog, styles.dialogWrapper), dataTestId: "qrAgeVerificationDialog", backgroundColor: backgroundColor, backgroundBlur: backgroundBlur, id: hardAgeVerificationModalId, portalDomNode: portalDomNode, children: _jsxs("div", { className: styles.container, children: [isTopBackButtonVisible && _jsx(BackButton, { onClick: handleBackButtonClick, buttonLabel: backButtonLabel }), _jsxs("div", { className: styles.wrapper, children: [_jsx("img", { src: logoSrc, alt: logoAlt, className: styles.logo }), _jsx("h2", { className: styles.title, children: title }), !isLarge && !isAgeVerificationInProgress && (_jsx(PopupImage, { imageSrc: modalImage, alt: alt, dataTestId: "disclaimerImage" })), descriptionContent && (_jsx("div", { className: styles.description, dangerouslySetInnerHTML: {
                                __html: sanitize(descriptionContent),
                            } })), isButtonsVisible && (_jsx(ActionButtons, { isError: !!errorMessage, isPassVerification: isPassVerification, primaryButtonLabel: primaryButtonLabel, secondaryButtonLabel: secondaryButtonLabel, isPrimaryButtonVisible: isPrimaryButtonVisible, onPrimaryButtonClick: handlePrimaryButtonClick, onSecondaryButtonClick: handleSecondaryButtonClick })), !isLarge && isAgeVerificationInProgress && (_jsx(AgeVerificationContainer, { ...renderAvMethodProps, isPortable: true })), legalNotice && (_jsx("div", { className: styles.legalNotice, children: _jsx("span", { dangerouslySetInnerHTML: {
                                    __html: sanitize(legalNotice),
                                } }) }))] }), isLarge && (_jsxs("div", { className: styles.functionalContainer, children: [_jsx(PopupImage, { imageSrc: modalImage, alt: alt, isAgeVerificationInProgress: isAgeVerificationInProgress, dataTestId: "disclaimerImage" }), isAgeVerificationInProgress && _jsx(AgeVerificationContainer, { ...renderAvMethodProps, isOverlay: true })] }))] }) }));
};
//# sourceMappingURL=HardAgeVerificationPopup.js.map